import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';

export function Navbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target) &&
        mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoadingSuggestions(true);
        const response = await fetch(`${API}/products?search=${encodeURIComponent(searchQuery)}&limit=5`);
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.products || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, API]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await fetch(`${API}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data || []);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [API]);

  const handleSuggestionClick = (productId) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/product/${productId}`);
  };

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 glass border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-2 md:px-8">
        <div className="flex items-center justify-between h-14 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-0" data-testid="logo-link">
            <img
              src="/logo.png"
              alt="Nso-India"
              className="h-7 md:h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center space-x-1 text-secondary hover:text-primary transition-colors font-body text-sm font-medium" data-testid="categories-dropdown">
                  <span>Categories</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 p-2">
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.category_id || cat.slug} asChild>
                    <Link to={`/category/${cat.slug || cat.category_id}`} className="flex flex-col py-3 px-3 cursor-pointer" data-testid={`cat-${cat.slug}`}>
                      <span className="font-medium text-secondary">{cat.name}</span>
                      {cat.description && <span className="text-xs text-muted-foreground line-clamp-1">{cat.description}</span>}
                    </Link>
                  </DropdownMenuItem>
                ))}
                {loadingCategories && categories.length === 0 && (
                  <div className="p-3 text-center text-xs text-muted-foreground animate-pulse">Loading categories...</div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {categories.slice(0, 4).map((cat) => (
              <Link 
                key={cat.category_id || cat.slug}
                to={`/category/${cat.slug || cat.category_id}`} 
                className="text-secondary hover:text-primary transition-colors font-body text-sm font-medium whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Search Bar - Desktop */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center flex-1 max-w-md mx-8 relative" ref={searchRef}>
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                className="pl-10 h-10 rounded-full bg-surface border-0 focus:ring-2 focus:ring-primary"
                data-testid="search-input"
              />
              {loadingSuggestions && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Desktop Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="py-2">
                  <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold border-b border-gray-50/50">Suggestions</div>
                  {suggestions.map((product) => (
                    <button
                      key={product.product_id}
                      type="button"
                      onClick={() => handleSuggestionClick(product.product_id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/5 transition-colors text-left group"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-50">
                        <img
                          src={getImageUrl(product.images?.[0])}
                          alt=""
                          onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-secondary truncate group-hover:text-primary transition-colors">
                          {product.name}
                        </p>
                        <p className="text-xs font-semibold text-primary">
                          {formatPrice(product.price)}
                        </p>
                      </div>
                    </button>
                  ))}
                  <button
                    type="submit"
                    className="w-full px-4 py-2 text-xs text-center text-primary bg-primary/5 hover:bg-primary/10 transition-colors font-medium"
                  >
                    View all results for "{searchQuery}"
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Right Icons */}
          <div className="flex items-center space-x-1 md:space-x-4">
            <Link to="/wishlist" className="relative p-2 hover:bg-surface rounded-full transition-colors" data-testid="wishlist-link">
              <Heart className="w-5 h-5 text-secondary" />
            </Link>

            <Link to="/cart" className="relative p-2 hover:bg-surface rounded-full transition-colors" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5 text-secondary" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-medium" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 hover:bg-surface rounded-full transition-colors" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=F4F4F5&color=18181B`;
                        }}
                      />
                    ) : (
                      <User className="w-5 h-5 text-secondary" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium text-secondary">{user.name}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user.role !== 'admin' ? (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard" data-testid="dashboard-link">My Dashboard</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/orders" data-testid="orders-link">My Orders</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard/settings" data-testid="settings-menu-link">Settings</Link>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="text-primary font-medium" data-testid="admin-link">Admin Panel</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-6 font-body text-sm"
                data-testid="login-btn"
              >
                Login
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-surface rounded-full transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="md:hidden pb-4 relative" ref={mobileSearchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
              className="pl-10 h-10 rounded-full bg-surface border-0"
              data-testid="mobile-search-input"
            />
            {loadingSuggestions && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Mobile Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200 mx-1">
              <div className="py-1">
                {suggestions.map((product) => (
                  <button
                    key={product.product_id}
                    type="button"
                    onClick={() => handleSuggestionClick(product.product_id)}
                    className="w-full flex items-center gap-3 px-3 py-3 active:bg-surface transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-gray-50">
                      <img src={getImageUrl(product.images?.[0])} alt="" onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary truncate">{product.name}</p>
                      <p className="text-xs font-semibold text-primary">{formatPrice(product.price)}</p>
                    </div>
                  </button>
                ))}
                <button
                  type="submit"
                  className="w-full px-4 py-3 text-xs text-center text-primary bg-surface font-medium border-t border-gray-50"
                >
                  See all results for "{searchQuery}"
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <div className="font-heading text-xs text-muted-foreground uppercase tracking-widest px-2 py-2 mb-1">Categories</div>
            {categories.map((cat) => (
              <Link
                key={cat.category_id || cat.slug}
                to={`/category/${cat.slug || cat.category_id}`}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-2 py-3 text-secondary hover:bg-surface rounded-xl transition-colors font-body font-medium"
                data-testid={`mobile-cat-${cat.slug}`}
              >
                {cat.name}
              </Link>
            ))}


            <div className="font-heading text-xs text-muted-foreground uppercase tracking-widest px-2 py-2 mb-1">Account</div>
            {user ? (
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-2 py-3 text-secondary hover:bg-surface rounded-xl transition-colors font-body font-medium flex items-center gap-3"
              >
                <User className="w-5 h-5 text-muted-foreground" /> My Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-2 py-3 text-secondary hover:bg-surface rounded-xl transition-colors font-body font-medium flex items-center gap-3"
              >
                <User className="w-5 h-5 text-muted-foreground" /> Login / Register
              </Link>
            )}
            <Link
              to="/wishlist"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-2 py-3 text-secondary hover:bg-surface rounded-xl transition-colors font-body font-medium flex items-center gap-3"
            >
              <Heart className="w-5 h-5 text-muted-foreground" /> Wishlist
            </Link>
            <Link
              to="/cart"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-2 py-3 text-secondary hover:bg-surface rounded-xl transition-colors font-body font-medium flex items-center gap-3"
            >
              <ShoppingCart className="w-5 h-5 text-muted-foreground" /> Cart ({cartCount})
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
